import React, { useState, useEffect } from 'react';
import { useLocation, useNavigate } from 'react-router-dom';
import { API_ENDPOINTS } from '../config/api';

const KidstoonPage = () => {
  const location = useLocation();
  const navigate = useNavigate();
  const student = location.state?.student;

  const [videos, setVideos] = useState([]);
  const [loading, setLoading] = useState(true);
  const [selectedVideo, setSelectedVideo] = useState(null);
  const [showVideoModal, setShowVideoModal] = useState(false);

  useEffect(() => {
    if (!student) {
      navigate('/student-login');
      return;
    }
    loadVideos();
  }, [student, navigate]);

  const loadVideos = async () => {
    try {
      const response = await fetch(`${API_ENDPOINTS.VIDEOS}`);
      const videosData = await response.json();
      setVideos(videosData);
    } catch (error) {
      console.error('Error loading videos:', error);
    } finally {
      setLoading(false);
    }
  };

  const getYouTubeVideoId = (url) => {
    const regExp = /^.*(youtu.be\/|v\/|u\/\w\/|embed\/|watch\?v=|&v=)([^#&?]*).*/;
    const match = url.match(regExp);
    return match && match[2].length === 11 ? match[2] : null;
  };

  const getEmbedUrl = (video) => {
    if (video.platform === 'youtube') {
      const videoId = getYouTubeVideoId(video.url);
      if (videoId) {
        // Add restrictive parameters to prevent external navigation and reduce branding
        const params = new URLSearchParams({
          modestbranding: '1',    // Reduce YouTube branding
          rel: '0',               // Only show related videos from same channel
          showinfo: '0',          // Hide video info (deprecated but still works)
          controls: '1',          // Show player controls
          disablekb: '0',         // Allow keyboard controls
          fs: '1',                // Allow fullscreen
          iv_load_policy: '3',    // Hide annotations
          cc_load_policy: '0',    // Hide captions by default
          autoplay: '0',          // Don't autoplay
          playsinline: '1',       // Play inline on mobile
          origin: window.location.origin // Set origin for security
        });
        return `https://www.youtube.com/embed/${videoId}Loading${params.toString()}`;
      }
    }
    // For other platforms, return the original URL
    return video.url;
  };

  const openVideoModal = (video) => {
    setSelectedVideo(video);
    setShowVideoModal(true);
  };

  const closeVideoModal = () => {
    setShowVideoModal(false);
    setSelectedVideo(null);
  };

  if (loading) {
    return (
      <div className="min-h-screen bg-blue-50 flex items-center justify-center">
        <div className="text-xl text-blue-600">Loading Kidstoon videos...</div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-blue-50">
      {/* Header */}
      <div className="bg-gradient-to-r from-purple-600 to-pink-600 text-white shadow-lg">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="flex justify-between items-center py-6">
            <div className="flex items-center space-x-4">
              <div className="w-12 h-12 bg-white bg-opacity-20 rounded-full flex items-center justify-center">
                <span className="text-2xl">Videos</span>
              </div>
              <div>
                <h1 className="text-3xl font-bold">Kidstoon</h1>
                <p className="text-purple-100">Safe educational videos for students</p>
              </div>
            </div>
            <button
              onClick={() => navigate('/student/dashboard', { state: { student } })}
              className="bg-white bg-opacity-20 text-white px-4 py-2 rounded-lg hover:bg-opacity-30 transition duration-300"
            >
              Back to Dashboard
            </button>
          </div>
        </div>
      </div>

      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-8">
        {/* Welcome Message */}
        <div className="bg-white rounded-lg shadow-lg p-6 mb-8">
          <div className="flex items-center space-x-4">
            <div className="w-16 h-16 bg-purple-100 rounded-full flex items-center justify-center">
              <span className="text-3xl">Video</span>
            </div>
            <div>
              <h2 className="text-2xl font-bold text-gray-900">
                Welcome to Kidstoon, {student.firstName}! Success
              </h2>
              <p className="text-gray-600 mt-1">
                Safe <strong>All videos are teacher-approved and safe!</strong> Stay within our school's secure learning environment.
                Click on any video below to start watching educational content.
              </p>
            </div>
          </div>
        </div>

        {/* Videos Grid */}
        {videos.length === 0 ? (
          <div className="bg-white rounded-lg shadow-lg p-12 text-center">
            <div className="text-8xl mb-4">Videos</div>
            <h3 className="text-2xl font-semibold text-gray-800 mb-2">No Videos Available Yet</h3>
            <p className="text-gray-600 mb-6">
              Your teachers haven't added any videos yet. Check back later for fun educational content!
            </p>
            <div className="text-4xl animate-bounce">Loading</div>
          </div>
        ) : (
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
            {videos.map((video) => (
              <div
                key={video.id}
                className="bg-white rounded-lg shadow-lg hover:shadow-xl transition-shadow duration-300 overflow-hidden cursor-pointer"
                onClick={() => openVideoModal(video)}
              >
                {/* Video Thumbnail Placeholder */}
                <div className="bg-gradient-to-br from-purple-400 to-pink-400 h-48 flex items-center justify-center">
                  <div className="text-white text-center">
                    <div className="text-4xl mb-2">Video</div>
                    <div className="text-sm font-medium">{video.platform.toUpperCase()}</div>
                  </div>
                </div>

                {/* Video Info */}
                <div className="p-6">
                  <h3 className="text-lg font-semibold text-gray-900 mb-2 line-clamp-2">
                    {video.title}
                  </h3>

                  {video.description && (
                    <p className="text-sm text-gray-600 mb-3 line-clamp-2">
                      {video.description}
                    </p>
                  )}

                  <div className="flex items-center justify-between text-xs text-gray-500">
                    <span>Added {new Date(video.addedDate).toLocaleDateString()}</span>
                    <span className="bg-purple-100 text-purple-800 px-2 py-1 rounded-full">
                      {video.platform}
                    </span>
                  </div>

                  {/* Play Button */}
                  <div className="mt-4 flex justify-center">
                    <button className="bg-purple-600 text-white px-6 py-2 rounded-full hover:bg-purple-700 transition duration-300 flex items-center space-x-2">
                      <span>Play</span>
                      <span>Watch Now</span>
                    </button>
                  </div>
                </div>
              </div>
            ))}
          </div>
        )}

        {/* Videos Summary */}
        {videos.length > 0 && (
          <div className="mt-8 text-center">
            <div className="bg-white rounded-lg shadow p-4">
              <p className="text-gray-600">
                Videos <strong>{videos.length}</strong> educational video{videos.length !== 1 ? 's' : ''} available for you to watch!
              </p>
            </div>
          </div>
        )}
      </div>

      {/* Video Modal */}
      {showVideoModal && selectedVideo && (
        <div className="fixed inset-0 bg-black bg-opacity-75 flex items-center justify-center z-50 p-4">
          <div className="bg-white rounded-lg shadow-2xl w-full max-w-4xl max-h-[90vh] overflow-hidden">
            {/* Modal Header */}
            <div className="bg-gradient-to-r from-purple-600 to-pink-600 text-white p-4">
              <div className="flex justify-between items-center">
                <div>
                  <h3 className="text-xl font-bold">{selectedVideo.title}</h3>
                  {selectedVideo.description && (
                    <p className="text-sm text-purple-100 mt-1">{selectedVideo.description}</p>
                  )}
                </div>
                <button
                  onClick={closeVideoModal}
                  className="text-white hover:text-purple-200 text-2xl"
                >
                  ✕
                </button>
              </div>
            </div>

            {/* Safety Warning */}
            <div className="px-4 py-2 bg-yellow-50 border-l-4 border-yellow-400">
              <div className="flex items-center">
                <div className="text-yellow-800">
                  <span className="font-medium">Safe Safety First:</span> All videos are approved by your teachers. Stay safe and enjoy learning!
                </div>
              </div>
            </div>

            {/* Video Player */}
            <div className="p-4">
              <div className="aspect-video bg-black rounded-lg overflow-hidden relative">
                {selectedVideo.platform === 'youtube' ? (
                  <>
                    <iframe
                      src={getEmbedUrl(selectedVideo)}
                      title={selectedVideo.title}
                      className="w-full h-full"
                      frameBorder="0"
                      allow="accelerometer; autoplay; clipboard-write; encrypted-media; gyroscope; picture-in-picture"
                      allowFullScreen
                    />
                    {/* Safety overlay to discourage external navigation */}
                    <div
                      className="absolute top-0 left-0 w-full h-full pointer-events-none"
                      style={{
                        background: 'transparent',
                        zIndex: 10
                      }}
                    />
                    {/* Additional safety message */}
                    <div className="absolute bottom-2 right-2 bg-black bg-opacity-75 text-white text-xs px-2 py-1 rounded pointer-events-none z-20">
                      Safe School-Safe Content
                    </div>
                  </>
                ) : (
                  <div className="w-full h-full flex items-center justify-center text-white">
                    <div className="text-center">
                      <div className="text-4xl mb-4">Locked</div>
                      <p className="mb-4 text-lg font-medium">External videos cannot be viewed directly</p>
                      <p className="mb-4 text-sm">For safety reasons, only approved embedded videos can be watched here.</p>
                      <div className="bg-red-600 text-white px-4 py-2 rounded-lg inline-block">
                        Locked Content Protected
                      </div>
                    </div>
                  </div>
                )}
              </div>
            </div>

            {/* Video Info */}
            <div className="px-4 pb-4">
              <div className="flex items-center justify-between text-sm text-gray-600">
                <span>Added by: {selectedVideo.addedBy}</span>
                <span>Platform: {selectedVideo.platform}</span>
              </div>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default KidstoonPage;




